import { useCallback, useRef, useState } from "react";

function joinUtterance(base, next) {
  const a = String(base || "").trim();
  const b = String(next || "").trim();
  if (!a) return b;
  if (!b) return a;
  return `${a} ${b}`;
}

/**
 * Cursor-style live speech-to-text draft.
 *
 * - While listening, interim text updates and self-corrects in real time.
 * - Stop commits the current utterance into the draft.
 * - Starting the mic again opens a fresh STT buffer for the next take.
 * - `resetBuffer()` (after send / validate) clears committed + live text so
 *   the next dictation starts empty.
 *
 * @param {{
 *   value: string,
 *   setValue: (next: string) => void,
 *   appendMode?: boolean,
 * }} opts
 *   appendMode — keep text committed before this listen and append the new take
 *   (notes). When false, each listen replaces the field (chat / Pingflo).
 */
export function useLiveVoiceTranscript({
  value,
  setValue,
  appendMode = false,
} = {}) {
  const valueRef = useRef(value);
  valueRef.current = value;
  const listeningRef = useRef(false);
  const committedRef = useRef("");
  const editingRef = useRef(false);
  const [listening, setListening] = useState(false);
  const [livePartial, setLivePartial] = useState("");

  const resetBuffer = useCallback(() => {
    committedRef.current = "";
    editingRef.current = false;
    listeningRef.current = false;
    setListening(false);
    setLivePartial("");
    setValue?.("");
  }, [setValue]);

  const markEditing = useCallback(() => {
    editingRef.current = true;
    committedRef.current = String(valueRef.current || "").trim();
    setLivePartial("");
  }, []);

  const onListeningChange = useCallback(
    (isListening) => {
      const on = Boolean(isListening);
      // Audio-level updates used to re-enter with listening still true and
      // wipe the draft the coach is about to send.
      if (on && listeningRef.current) return;
      setListening(on);
      listeningRef.current = on;
      if (!on) return;

      // Fresh recognition buffer for this take (BrowserSpeechService also clears).
      editingRef.current = false;
      setLivePartial("");
      if (appendMode) {
        committedRef.current = String(valueRef.current || "").trim();
      } else {
        committedRef.current = "";
        setValue?.("");
      }
    },
    [appendMode, setValue]
  );

  const onPartial = useCallback(
    (text) => {
      if (!listeningRef.current || editingRef.current) return;
      const cleaned = String(text || "").trim();
      setLivePartial(cleaned);
      setValue?.(joinUtterance(committedRef.current, cleaned));
    },
    [setValue]
  );

  const onTranscript = useCallback(
    ({ text } = {}) => {
      const cleaned = String(text || "").trim();
      listeningRef.current = false;
      setListening(false);
      setLivePartial("");
      if (editingRef.current) return;
      const next = joinUtterance(committedRef.current, cleaned);
      committedRef.current = next;
      if (next) setValue?.(next);
    },
    [setValue]
  );

  return {
    listening,
    livePartial,
    onPartial,
    onTranscript,
    onListeningChange,
    resetBuffer,
    markEditing,
  };
}

export default useLiveVoiceTranscript;
